import {
  FC,
  lazy,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { FormattedMessage } from 'react-intl';

import { IAttachmentPreviewModalProps } from './attachment-preview.interface';
import styles from './attachment-preview.module.scss';

import { Button } from 'views/components/UI/Button';
import { DialogFooter, Modal } from 'views/components/UI/Modal';
import { Text } from 'views/components/UI/Text';

const AttachmentPreviewContent = lazy(() => import('./AttachmentPreviewContent'));

type PreviewStatus = 'error' | 'loading' | 'ready';

const DOWNLOAD_URL_RELEASE_DELAY_MS = 60000;

const AttachmentPreviewModal: FC<IAttachmentPreviewModalProps> = ({
  contentType,
  file,
  fileName,
  handleClose,
  isOpen,
  requestDownloadUrl,
  sizeBytes,
}): ReactElement => {
  const requestIdRef = useRef(0);
  const [downloadUrl, setDownloadUrl] = useState<string>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [status, setStatus] = useState<PreviewStatus>(file ? 'ready' : 'loading');

  const loadRemoteFile = useCallback(async (): Promise<void> => {
    if (!requestDownloadUrl) {
      setStatus('error');
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setDownloadUrl(undefined);
    setStatus('loading');

    try {
      const nextDownloadUrl = await requestDownloadUrl();
      if (requestIdRef.current === requestId) {
        setDownloadUrl(nextDownloadUrl);
        setStatus('ready');
      }
    } catch {
      if (requestIdRef.current === requestId) {
        setStatus('error');
      }
    }
  }, [requestDownloadUrl]);

  useEffect(() => {
    if (!isOpen) {
      requestIdRef.current += 1;
      setDownloadUrl(undefined);
      setStatus(file ? 'ready' : 'loading');
      return undefined;
    }

    if (!file) {
      loadRemoteFile();
    }

    return () => {
      // Ignore a signed-URL request that resolves after the modal unmounts,
      // closes, or switches to a different attachment.
      requestIdRef.current += 1;
    };
  }, [file, isOpen, loadRemoteFile]);

  const handleDownload = async (): Promise<void> => {
    try {
      setIsDownloading(true);

      if (file) {
        const objectUrl = URL.createObjectURL(file);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(
          () => URL.revokeObjectURL(objectUrl),
          DOWNLOAD_URL_RELEASE_DELAY_MS,
        );
        return;
      }

      if (requestDownloadUrl) {
        const freshDownloadUrl = await requestDownloadUrl();
        window.open(freshDownloadUrl, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      modalTitle="attachments.preview.title"
      valueTitle={fileName}
      size="fullscreen"
    >
      <div className={styles.layout}>
        <div className={styles.content}>
          {status === 'loading' && (
            <Text align="center">
              <FormattedMessage id="attachments.preview.loading" />
            </Text>
          )}
          {status === 'error' && (
            <div className={styles.message}>
              <Text align="center">
                <FormattedMessage id="attachments.preview.error" />
              </Text>
              {!file && (
                <Button mode="secondary" size="small" onClick={loadRemoteFile}>
                  <FormattedMessage id="attachments.preview.retry" />
                </Button>
              )}
            </div>
          )}
          {status === 'ready' && (file || downloadUrl) && (
            <Suspense
              fallback={
                <Text align="center">
                  <FormattedMessage id="attachments.preview.loading-viewer" />
                </Text>
              }
            >
              <AttachmentPreviewContent
                contentType={contentType}
                downloadUrl={downloadUrl}
                file={file}
                fileName={fileName}
                onError={() => setStatus('error')}
                sizeBytes={sizeBytes}
              />
            </Suspense>
          )}
        </div>
        <DialogFooter>
          <Button mode="secondary" onClick={handleClose}>
            <FormattedMessage id="attachments.preview.close" />
          </Button>
          <Button onClick={handleDownload} disabled={isDownloading}>
            <FormattedMessage id="attachments.preview.download" />
          </Button>
        </DialogFooter>
      </div>
    </Modal>
  );
};

export { AttachmentPreviewModal };
